import React, { useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  InputLabel,
  TextField,
  useMediaQuery,
  useTheme,
  Link as MuiLink,
} from "@mui/material";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import { AccountRoutePaths, PARTNER_INVITE_STORAGE_KEY } from "../routes";
import { useAppDispatch, useAppSelector } from "../../shared/hooks/sharedHooks";
import MachineLogoIcon from "../../shared/assets/icons/MachineLogoIcon";
import colors from "../../shared/themes/colors";
import { emailRegex } from "../../shared/validators";
import FormNotificationMessage from "../../shared/components/FormNotificationMessage";
import Loader from "../../shared/components/Loader";
import EmailSentConfirmation from "./EmailSentConfirmation";
import useTranslation from "../../shared/hooks/useTranslation";
import { Register } from "../../customer/shared/models/customerModels";
import { startRegistration, resetState } from "../startRegistrationSlice";
import { dt } from "../../shared/themes/redesignTokens";

const StartRegistrationPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { translate, getCurrentLanguage } = useTranslation("account");
  const navigate = useNavigate();
  const { search } = useLocation();
  const dispatch = useAppDispatch();

  const { isLoading, isConfirmEmail, formNotificationMessage, email } = useAppSelector(
    (state) => state.startRegistration
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Register>();

  const onSubmit: SubmitHandler<Register> = (model: Register) => {
    // EMAIL-LANG-WIRING: send the UI locale so the confirmation email arrives localized.
    dispatch(startRegistration({ ...model, language: getCurrentLanguage() }));
  };

  useEffect(() => {
    dispatch(resetState());

    // Store partner invite token in sessionStorage if present in URL
    const params = new URLSearchParams(search);
    const partnerInviteToken = params.get("partnerInvite");
    if (partnerInviteToken) {
      sessionStorage.setItem(PARTNER_INVITE_STORAGE_KEY, partnerInviteToken);
    }
  }, [dispatch, search]);

  if (isConfirmEmail) {
    return (
      <EmailSentConfirmation
        title={translate("confirmEmailTitle")}
        description={
          <>
            {translate("confirmEmailDescriptionStart")}
            <Box component={"span"} sx={{ fontWeight: 700, wordBreak: 'break-all', overflowWrap: 'break-word', maxWidth: '100%' }}>{email}</Box>
            {translate("confirmEmailDescriptionEnd")}
          </>
        }
        open
        hasLogo
        onClose={() => navigate(AccountRoutePaths.signIn)}
      />
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        paddingY: isMobile ? "32px" : "64px",
        backgroundColor: colors.secondary.gray98,
      }}
    >
      <Card
        sx={{
          maxWidth: "600px",
          width: "100%",
          margin: "0 16px",
          display: "flex",
          flexDirection: "column",
          borderRadius: "8px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          padding: isMobile ? "32px 16px 32px" : "64px",
          gap: "48px",
        }}
      >
        {isLoading ? (
          <Box sx={{ width: '100%', minHeight: '420px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loader />
          </Box>
        ) : (
          <>
            <Box className="flex flex-col items-center text-center">
              <MachineLogoIcon />
              <Box
                sx={{
                  color: colors.primary.violetDark,
                  fontSize: "36px",
                  lineHeight: "42px",
                  letterSpacing: "-0.072px",
                  fontWeight: 500,
                  textAlign: "center",
                  paddingX: "40px",
                  ...(isMobile && {
                    fontSize: "28px",
                    lineHeight: "34px",
                    marginTop: "16px",
                    textAlign: "center",
                    paddingX: "20px",
                  })
                }}
              >
                {translate("signUpToAccess")}
              </Box>
            </Box>

            <CardContent sx={{ padding: 0 }}>
              {formNotificationMessage && (
                <Box className="mb-3 mt-2">
                  <FormNotificationMessage {...formNotificationMessage} />
                </Box>
              )}

              <Box
                component="form"
                id="startRegistrationPageForm"
                onSubmit={handleSubmit(onSubmit)}
                noValidate
              >
                <InputLabel>{translate("yourEmail")}</InputLabel>
                <TextField
                  fullWidth
                  type="email"
                  variant="outlined"
                  placeholder="mail@mail.com"
                  {...register("userName", { required: true, pattern: emailRegex })}
                  error={!!errors.userName}
                  helperText={errors.userName ? translate("invalidEmail") : ""}
                />

                <Box sx={{ width: "100%", mt: 6 }}>
                  <Button type="submit" fullWidth variant="contained">
                    {translate("signUpWithEmail")}
                  </Button>
                </Box>
              </Box>

              <Box className="flex flex-col gap-12" sx={{ mt: 3 }}>
                <Box className="text-16-regular" sx={{ display: "inline", color: colors.secondary.gray20 }}>
                  {translate("byContinuing")}
                  <MuiLink href="/terms" target="_blank" sx={{ textDecoration: "underline", textTransform: "capitalize" }}>
                    {translate("termsOfService")}
                  </MuiLink>
                  {translate("andAcknowledge")}
                  <MuiLink href="/privacy" target="_blank" sx={{ textDecoration: "underline", textTransform: "capitalize" }}>
                    {translate("privacyPolicy")}
                  </MuiLink>
                </Box>

                <Box className="flex items-center justify-center">
                  <Box component={"span"} className="text-16-regular" sx={{ color: colors.secondary.gray20 }}>
                    {translate("alreadyMember")}
                  </Box>
                  <Button variant="text" onClick={() => navigate(AccountRoutePaths.signIn)}>
                    <Box className="text-16-regular" sx={{ textDecoration: "underline", color: dt.blue }}>
                      {translate("logIn")}
                    </Box>
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </>
        )}
      </Card>
    </Box>
  );
};

export default StartRegistrationPage;


